import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api, { API_BASE } from "../../../../utils/api";
import {
  normalizeCategories,
  normalizeSubcategories,
  formatDateForInput,
  validateImageFile,
  buildProductFormData,
} from "../utils/productUtils";
import {
  PRODUCT_STATUS,
  VALIDATION_MESSAGES,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES,
} from "../utils/constants";

/**
 * Custom hook for product add/edit form logic
 * Manages all state and side effects for the product form
 */
export const useProductForm = (id) => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [status, setStatus] = useState(PRODUCT_STATUS.OPEN);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [languages, setLanguages] = useState([]);
  const [enableMultiLang, setEnableMultiLang] = useState(false);
  const [selectedLang, setSelectedLang] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  // const [hasSubcategory, setHasSubcategory] = useState(false);

  const [formData, setFormData] = useState({
    productName: { en: "" },
    productNumber: "",
    description: { en: "" },
    category: "",
    subcategory: "",
    subcategoryName: "",
    netQuantity: 1,
    unit: "plate",
    manufactureDate: "",
    expiryDate: "",
    price: "",
    packageCharge: "",
    offerPrice: "",
    image: null,
    isVisible: false,
    // isVeg: false,
    isTaxable: true,
    online: true,
    foodType: "na",
  });

  // Fetch languages on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/languages/availableLanguages`)
      .then((res) => res.json())
      .then((data) => setLanguages(data.filter((l) => l.code !== "en")))
      .catch(() => toast.error(ERROR_MESSAGES.FETCH_LANGUAGES_FAILED));
  }, []);

  // Fetch categories and product data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoryResponse = await api.get("/categories/all");
        const normalizedCategories = normalizeCategories(categoryResponse.data);
        setCategories(normalizedCategories);

        if (id) {
          const productResponse = await api.get(`/products/view/${id}`);
          const product = productResponse.data;
          const variants = product.variants || [];
          const isMultipleVariants = variants.length > 1;
          const singleVariant = variants.length === 1 ? variants[0] : null;

          setFormData({
            ...product,
            productName:
              typeof product.productName === "string"
                ? { en: product.productName }
                : product.productName || { en: "" },
            description:
              typeof product.description === "string"
                ? { en: product.description }
                : product.description || { en: "" },
            subcategory: product.subcategory || "",
            isVisible: product.isVisible === true,
            // isVeg: product.isVeg === true,
            foodType:
              product.foodType ||
              (product.isVeg === true
                ? "veg"
                : product.isVeg === false
                  ? "nonveg"
                  : "na"),
            isTaxable: product.isTaxable !== false,
            online: product.online !== false,
            netQuantity: singleVariant ? singleVariant.quantity : "",
            unit: isMultipleVariants
              ? ""
              : product.unit || singleVariant?.unit || "",
            price: isMultipleVariants
              ? ""
              : product.price || singleVariant?.price || "",
            offerPrice: isMultipleVariants
              ? ""
              : product.offerPrice || singleVariant?.offerPrice || "",
            manufactureDate: formatDateForInput(
              product.manufactureDate || singleVariant?.manufactureDate,
            ),
            expiryDate: formatDateForInput(
              product.expiryDate || singleVariant?.expiryDate,
            ),
          });

          setStatus(product.status);
          // if (product.category && product.subcategory) {
          //   setHasSubcategory(product.subcategory !== product.category);
          // }

          if (product.category) {
            fetchSubcategories(product.category);
          }
        }
      } catch (err) {
        console.error(ERROR_MESSAGES.FETCH_CATEGORIES_FAILED, err);
      }
    };

    fetchData();
  }, [id]);

  // Fetch subcategories
  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await api.get(`/subcategory/${categoryId}`);
      const normalizedSubcategories = normalizeSubcategories(response.data);
      setSubcategories(normalizedSubcategories);
    } catch (err) {
      console.error(ERROR_MESSAGES.FETCH_SUBCATEGORIES_FAILED, err);
    }
  };

  // Handle category change
  const handleCategoryChange = async (e) => {
    const selectedCategoryId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      category: selectedCategoryId,
      subcategory: "",
      subcategoryName: "",
    }));
    // setHasSubcategory(false);

    if (selectedCategoryId) {
      await fetchSubcategories(selectedCategoryId);
    } else {
      setSubcategories([]);
    }
  };

  // Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const validation = validateImageFile(file);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setErrors({});

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setFormData((prevData) => ({ ...prevData, image: file }));
  };

  // Handle status change (accept/reject)
  const handleStatusChange = async (action) => {
    if (isStatusChanging) return; // Prevent multiple clicks

    try {
      setIsStatusChanging(true);
      const username = localStorage.getItem("username");

      if (!username) {
        toast.error(VALIDATION_MESSAGES.USERNAME_MISSING);
        setIsStatusChanging(false);
        return;
      }

      const response = await api.post(`/products/${action}/${id}`, {
        username,
      });
      toast.success(response.data.message);
      navigate("/product");

      if (action === "accept") setStatus(PRODUCT_STATUS.APPROVED);
      if (action === "reject") setStatus(PRODUCT_STATUS.REJECTED);
    } catch (err) {
      console.error("Error:", err.response?.data || err.message);
      toast.error(
        err.response?.data?.message || ERROR_MESSAGES.STATUS_UPDATE_FAILED,
      );
    } finally {
      setIsStatusChanging(false);
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    // Validate form
    if (
      !formData.price ||
      isNaN(formData.price) ||
      Number(formData.price) <= 0
    ) {
      toast.warning(VALIDATION_MESSAGES.PRICE_REQUIRED);
      return;
    }

    if (!formData.category?.trim()) {
      toast.warning(VALIDATION_MESSAGES.CATEGORY_REQUIRED);
      return;
    }

    // If no subcategory selected → save as category (No Subcategory)
    if (!formData.subcategory && formData.category) {
      formData.subcategory = formData.category;
    }

    if (
      !formData.description?.en ||
      formData.description.en.trim().length < 5
    ) {
      toast.warning(VALIDATION_MESSAGES.DESCRIPTION_REQUIRED);
      return;
    }

    if (!formData.productName?.en || formData.productName.en.trim() === "") {
      toast.warning(VALIDATION_MESSAGES.PRODUCT_NAME_REQUIRED);
      return;
    }

    // Validate image for new products only
    if (status === PRODUCT_STATUS.OPEN && (!isEditing || !id)) {
      if (!formData.image) {
        toast.warning(VALIDATION_MESSAGES.IMAGE_REQUIRED);
        return;
      }

      const validation = validateImageFile(formData.image);
      if (!validation.isValid) {
        toast.warning(Object.values(validation.errors)[0]);
        return;
      }
    }

    // Set submitting state after all validations pass
    setIsSubmitting(true);

    // Check for duplicates (new products only)
    if (!isEditing || !id) {
      try {
        const checkResponse = await api.post(`/products/check-product`, {
          productName: formData.productName.en,
          productNumber: formData.productNumber,
        });

        if (checkResponse.data.exists) {
          if (checkResponse.data.duplicateField === "productNumber") {
            toast.warning(VALIDATION_MESSAGES.DUPLICATE_PRODUCT_NUMBER);
          } else if (checkResponse.data.duplicateField === "productName") {
            toast.warning(VALIDATION_MESSAGES.DUPLICATE_PRODUCT_NAME);
          } else {
            toast.warning(VALIDATION_MESSAGES.DUPLICATE_PRODUCT);
          }
          setIsSubmitting(false);
          return;
        }
      } catch (err) {
        console.error(
          "Error checking product:",
          err.response?.data || err.message,
        );
        if (err.response?.data?.duplicateField === "productNumber") {
          toast.warning(VALIDATION_MESSAGES.DUPLICATE_PRODUCT_NUMBER);
        } else if (err.response?.data?.duplicateField === "productName") {
          toast.warning(VALIDATION_MESSAGES.DUPLICATE_PRODUCT_NAME);
        } else {
          toast.error("Error: " + (err.response?.data?.message || err.message));
        }
        setIsSubmitting(false);
        return;
      }
    }

    // Submit product
    try {
      const data = buildProductFormData(formData, isEditing, id);
      let response;

      if (isEditing && id) {
        response = await api.put(`/products/${id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.status === 200) {
          toast.success(SUCCESS_MESSAGES.PRODUCT_UPDATED);
          setTimeout(() => navigate("/product"), 2000);
          setIsEditing(false);
        } else {
          toast.error(ERROR_MESSAGES.PRODUCT_UPDATE_FAILED);
        }
      } else {
        response = await api.post(`/products/add-product`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.status === 201) {
          toast.success(SUCCESS_MESSAGES.PRODUCT_CREATED);
          setTimeout(() => navigate("/product"), 2000);
          setStatus(PRODUCT_STATUS.PENDING);
        } else {
          toast.error(ERROR_MESSAGES.PRODUCT_CREATE_FAILED);
        }
      }
    } catch (err) {
      console.error(
        "Error submitting product:",
        err.response?.data || err.message,
      );
      toast.error("Error: " + (err.response?.data?.message || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    imagePreview,
    categories,
    subcategories,
    status,
    isEditing,
    setIsEditing,
    errors,
    languages,
    enableMultiLang,
    setEnableMultiLang,
    selectedLang,
    setSelectedLang,
    isSubmitting,
    isStatusChanging,
    // hasSubcategory,       
    // setHasSubcategory,     

    handleCategoryChange,
    handleFileChange,
    handleStatusChange,
    handleSubmit,
  };
};
